﻿import { useQuery } from '@tanstack/react-query';
import { faTasks } from '@fortawesome/free-solid-svg-icons';
import { formatTime12Hour } from '@s20/common/util/date';
import { getBitmaskLabels } from '@s20/common/util/number';
import { httpClient } from '@s20/services/httpClient';
import { getTenantSetting } from '@s20/selectors/features/common.selectors';
import { useInstanceSelector } from '@s20/hooks/redux.hooks';
import {
    ApiRoutes,
    EmployeePropertyDisplayNames,
    WeekdayAbbreviations,
} from '../Common';
import SlideForm from '../common/forms/SlideForm';
import HistorySection from '../common/history/HistorySection';
import HistoryTypeSelect from '../common/history/HistoryTypeSelect';
import { TenantSettings } from '../tenant/Tenant';

const restrictionTypeLabels = {
    1: 'Customer',
    2: 'Employee',
    3: 'Time',
};

const formatRestrictionValue = (property, value) => {
    if (property === 'EmployeeRestrictionTypeId') {
        return restrictionTypeLabels[value] ?? '';
    }

    if (property === 'RestrictionDays') {
        return getBitmaskLabels(value, WeekdayAbbreviations).join(', ');
    }

    if (property === 'RestrictionStartTime' || property === 'RestrictionEndTime') {
        return formatTime12Hour(value);
    }

    return value;
};

const employeeHistorySections = [
    {
        key: 'groups',
        heading: 'Groups',
    },
    {
        key: 'general',
        heading: 'General Information',
        renderOptions: { propertyDisplayNames: EmployeePropertyDisplayNames },
    },
    {
        key: 'payRate',
        heading: 'Pay Rate',
        renderOptions: { simpleDate: true },
    },
    {
        key: 'addresses',
        heading: 'Addresses',
    },
    {
        key: 'restrictions',
        heading: 'Restrictions',
        renderOptions: {
            formatValue: formatRestrictionValue,
            propertyDisplayNames: EmployeePropertyDisplayNames,
        },
    },
    {
        key: 'longTermStatuses',
        heading: 'Long-Term Statuses',
        renderOptions: { simpleDate: true },
    },
    {
        key: 'countyGroups',
        heading: 'County Groups',
        renderOptions: { propertyDisplayNames: EmployeePropertyDisplayNames },
    },
    {
        key: 'countyIncentives',
        heading: 'County Incentives',
        renderOptions: {
            simpleDate: true,
            propertyDisplayNames: EmployeePropertyDisplayNames,
        },
    },
];

const baseEmployeeHistoryTypeOptions = [
    { value: 'all', label: 'All versions' },
    ...employeeHistorySections.map((section) => ({
        value: section.key,
        label: section.filterLabel ?? section.heading,
    })),
];

const EmployeeHistoryForm = ({
    id = 'employeeHistoryForm', show, employeeId, historyType = null, onClose,
}) => {
    const historyTypeIsValid = employeeHistorySections.some(
        (section) => section.key === historyType,
    );
    const initialSection = historyTypeIsValid ? historyType : 'all';

    const { data: fullHistory = {}, isFetching: loading } = useQuery({
        queryKey: ['employeeFullHistory', employeeId],
        queryFn: () => httpClient.js(ApiRoutes.employeeHistory.byId(employeeId)),
        enabled: show && Boolean(employeeId),
    });

    const enforceContractUnionWork = useInstanceSelector(getTenantSetting, TenantSettings.EnforceContractUnionWork);
    const employeeHistoryTypeOptions = enforceContractUnionWork ? baseEmployeeHistoryTypeOptions : baseEmployeeHistoryTypeOptions.filter((x) => x.value !== 'countyGroups' && x.value !== 'countyIncentives');

    return (
        <SlideForm
            loading={loading}
            show={show}
            id={id}
            formIcon={faTasks}
            formTitle="Employee History"
            onClose={onClose}
            initialValues={{ versionHistorySection: initialSection }}
            saveDisabled
            className="history-form"
        >
            {({ values }) => {
                const selectedSection = values.versionHistorySection ?? 'all';

                return (
                    <div className="w-100">
                        <HistoryTypeSelect options={employeeHistoryTypeOptions} />

                        <HistorySection
                            historicalChangeCollection={fullHistory[selectedSection]}
                        />
                    </div>
                );
            }}
        </SlideForm>
    );
};

export default EmployeeHistoryForm;
